import cn from 'classnames';
import { FC, useCallback } from 'react';
import {
  ErrorCode,
  FileRejection,
  FileWithPath,
  useDropzone,
} from 'react-dropzone';
import { Control, Controller, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import gladCat from '@/shared/assets/images/cats/glad.png';
import intraoralScanPlaceholder from '@/shared/assets/images/intraoralScanPlaceholder.png';
import {
  ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE,
  ACCEPTED_FILE_TYPES_FOR_DROPZONE_FOR_IOS_SEGMENTATION_REPORT,
} from '@/shared/config';
import { OrderReportType, UploadStudyType } from '@/shared/config/types';
import { useAppSelector, useMedia } from '@/shared/hooks';
import { isDicomFile } from '@/shared/lib';
import { Icon, InlineFormError, Tooltip } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { StudyAccordionSelect } from '@/entities/study';

import {
  MAX_BYTES_FOR_CBCT,
  MAX_BYTES_FOR_STUDIES,
  OrderReportModalStep,
  SELECT_INPUT_NAME,
  UploadStudyPayload,
} from '../../config';
import { selectUploadStudyTypes } from '../../config/i18n';
import {
  getStudyImageSrc,
  hasAcceptedExtension,
  hasKnownFileExtension,
  shouldShowPlaceholder,
} from '../../lib';
import { selectFilteredStudies } from '../../model/orderReportModal.selectors';
import { OrderReportDropzone } from '../OrderReportDropzone/OrderReportDropzone';
import { UploadStudyFile } from '../UploadStudyFile/UploadStudyFile';

import styles from './OrderReportStep.module.scss';

type OrderReportStepProps = {
  control: Control<UploadStudyPayload, unknown>;
  step?: OrderReportModalStep;
  orderReportType: OrderReportType;
  studyType: StudyType;
  assetType: UploadStudyType;
  files: FileWithPath[];
  currentStudyID?: string;
  errorMessage?: string;
  isOptional?: boolean;
  onlyUpload?: boolean;
  hideTitle?: boolean;
  infoTooltip?: { show: boolean; message: string };
  isAlignmentCBCTReportOrder?: boolean;
  className?: string;
  testID?: string;
  onAddFiles: (files: FileWithPath[]) => void;
  onDeleteFiles: () => void;
  onError: (errorMessage: string) => void;
  onReset?: () => void;
};

export const OrderReportStep: FC<OrderReportStepProps> = (props) => {
  const {
    className,
    testID,
    step,
    orderReportType,
    studyType,
    assetType,
    files,
    control,
    currentStudyID,
    errorMessage,
    isOptional,
    infoTooltip,
    onlyUpload,
    hideTitle,
    isAlignmentCBCTReportOrder,
    onAddFiles,
    onDeleteFiles,
    onError,
    onReset,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const mainStudyID = useWatch({
    control,
    name: SELECT_INPUT_NAME[1],
  });

  const filteredStudies = useAppSelector(
    selectFilteredStudies({
      studyType,
      mainStudyID: mainStudyID ?? '',
      isAlignmentCBCTReportOrder: isAlignmentCBCTReportOrder ?? false,
    }),
  );

  const isForbidUploadsFromWeb = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Forbid_UploadsFromWeb,
    ),
  );

  const isIOSSegmentationOrderReport = orderReportType === 'IOSSegmentation';
  const isPanoOrderReport = orderReportType === 'Pano';
  const isStudySelected = Boolean(currentStudyID);
  const isIOSMeshesStudy = studyType === StudyType.StudyType_IOS_Meshes;
  const previewPlaceholder = isIOSMeshesStudy
    ? intraoralScanPlaceholder
    : gladCat;
  const actualStudyExists = filteredStudies.length > 0;
  const filesExist = files.length > 0;
  const showStudySelect = actualStudyExists && !filesExist && !onlyUpload;
  const showDropzone =
    !isForbidUploadsFromWeb &&
    ((!actualStudyExists && !filesExist) || (!filesExist && onlyUpload));
  const showUploadStudyFile = filesExist && !isForbidUploadsFromWeb;
  const isMoreThanOneStep = step !== undefined;

  const hasContentToShow =
    showStudySelect || showDropzone || showUploadStudyFile;

  const handleDropAccepted = async (acceptedFiles: File[]) => {
    const config = isIOSSegmentationOrderReport
      ? ACCEPTED_FILE_TYPES_FOR_DROPZONE_FOR_IOS_SEGMENTATION_REPORT
      : ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE[assetType];

    const isCBCT = assetType === AssetType.AssetType_Study_CBCT;
    const isIOXray = assetType === AssetType.AssetType_Study_IntraoralXRay;
    const isPano = assetType === AssetType.AssetType_Study_PanoramicXRay;

    const validateFile = async (file: File): Promise<File | null> => {
      if (hasAcceptedExtension(file, config)) {
        return file;
      }

      if ((isCBCT || isIOXray || isPano) && !hasKnownFileExtension(file.name)) {
        const isDicom = await isDicomFile(file);

        return isDicom ? file : null;
      }

      return null;
    };

    const results = await Promise.all(acceptedFiles.map(validateFile));
    const filteredFiles = results.filter((file): file is File => file !== null);

    if (acceptedFiles.length > 0 && filteredFiles.length === 0) {
      onError(
        formatMessage({
          id: 'orderReportModal.wrongFile',
          defaultMessage: 'Wrong File(s) format',
        }),
      );

      return;
    }

    onAddFiles(filteredFiles);
  };

  const handleRejectFiles = useCallback(
    (fileRejections: FileRejection[]) => {
      let currentErrorMessage: string;

      if (fileRejections.length > 0) {
        const errorCode = fileRejections?.at(0)?.errors?.at(0)?.code;

        switch (errorCode) {
          case ErrorCode.FileInvalidType: {
            currentErrorMessage = formatMessage({
              id: 'orderReportModal.wrongFile',
              defaultMessage: 'Wrong File(s) format',
            });
            break;
          }
          case ErrorCode.FileTooLarge: {
            const maxSize =
              assetType === AssetType.AssetType_Study_CBCT ? '10GB' : '250MB';

            currentErrorMessage = formatMessage(
              {
                id: 'orderReportModal.sizeError',
                defaultMessage: 'File(s) size must be less than {maxSize}',
              },
              { maxSize },
            );
            break;
          }
          default: {
            currentErrorMessage = formatMessage({
              id: 'uploadImage.wrongFile',
              defaultMessage: 'Some files are too large or incorrect format',
            });
            break;
          }
        }

        onError(currentErrorMessage);
      }
    },
    [assetType, formatMessage, onError],
  );

  const dropzoneState = useDropzone({
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleRejectFiles,
    maxSize:
      studyType === StudyType.StudyType_CBCT
        ? MAX_BYTES_FOR_CBCT
        : MAX_BYTES_FOR_STUDIES,
    accept:
      assetType === AssetType.AssetType_Study_DentalPhoto
        ? ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE[assetType]
        : { '*/*': ['*'] },
    multiple: !isPanoOrderReport,
  });

  if (!hasContentToShow && !errorMessage) {
    return null;
  }

  return (
    <div
      data-testid={testID}
      className={cn(styles.container, className)}
      data-component="order-report-step"
    >
      {!hideTitle && (
        <h5 className={cn(styles.title, isMobile ? 'p3' : 'h5')}>
          {isMoreThanOneStep && !isAlignmentCBCTReportOrder && (
            <FormattedMessage
              id="orderReportModal.step"
              defaultMessage="Step {step}:"
              values={{ step }}
            />
          )}
          {isMoreThanOneStep && ' '}
          {!isForbidUploadsFromWeb && (!actualStudyExists || onlyUpload) ? (
            <FormattedMessage
              id="orderReportModal.uploadSubtitle"
              defaultMessage="Upload"
            />
          ) : (
            <FormattedMessage
              id="orderReportModal.selectSubtitle"
              defaultMessage="Select"
            />
          )}{' '}
          {isAlignmentCBCTReportOrder ? (
            <FormattedMessage
              id="orderReportModal.additionalCBCTSubtitle"
              defaultMessage="previous CBCT for alignment"
            />
          ) : (
            formatMessage(selectUploadStudyTypes[assetType])
          )}{' '}
          {isOptional &&
            `(${formatMessage({
              id: 'global.optional',
              defaultMessage: 'Optional',
            }).toLowerCase()})`}
          {infoTooltip?.show && (
            <Tooltip.Primary
              content={infoTooltip.message}
              triggerClassName={styles.infoTooltip}
            >
              <Icon name="info" size={28} className={styles.infoIcon} />
            </Tooltip.Primary>
          )}
        </h5>
      )}

      {showStudySelect && (
        <Controller
          control={control}
          name={SELECT_INPUT_NAME[step ?? 1]}
          render={({ field: { value, name, onChange } }) => (
            <StudyAccordionSelect
              name={name}
              value={value}
              options={filteredStudies.map((study) => ({
                id: study.id,
                previewSrc: getStudyImageSrc(
                  isIOSMeshesStudy,
                  previewPlaceholder,
                  study.previewSrc,
                ),
                isPlaceholder: shouldShowPlaceholder(
                  isIOSMeshesStudy,
                  study.previewSrc,
                ),
                contain: studyType === StudyType.StudyType_IntraoralXRay,
                naturalHeight: isIOSMeshesStudy,
                triggerLabel: (
                  <FormattedDate value={study.createdAt} dateStyle="medium" />
                ),
                title: (
                  <>
                    {formatMessage(selectUploadStudyTypes[assetType])}{' '}
                    <FormattedDate value={study.createdAt} />
                  </>
                ),
                description: (
                  <FormattedMessage
                    id="orderReportModal.createdAt"
                    defaultMessage="Created at {date}"
                    values={{
                      date: (
                        <FormattedDate
                          value={study.createdAt}
                          dateStyle="medium"
                          timeStyle="short"
                        />
                      ),
                    }}
                  />
                ),
              }))}
              placeholder={
                <FormattedMessage
                  id="orderReportModal.selectSubtitle"
                  defaultMessage="Select"
                />
              }
              renderTrailing={(isOpen) =>
                isOptional &&
                isStudySelected && (
                  <Icon
                    name="delete"
                    size={32}
                    className={cn(
                      styles.deleteIcon,
                      isOpen && styles.fadeOutDeleteIcon,
                    )}
                    onClick={onReset}
                  />
                )
              }
              onChange={onChange}
            >
              {!isForbidUploadsFromWeb && (
                <OrderReportDropzone
                  dropzoneState={dropzoneState}
                  canUploadFolder={!isPanoOrderReport}
                />
              )}
            </StudyAccordionSelect>
          )}
        />
      )}

      {showDropzone && (
        <OrderReportDropzone
          dropzoneState={dropzoneState}
          canUploadFolder={!isPanoOrderReport}
        />
      )}

      {showUploadStudyFile && (
        <UploadStudyFile
          files={files}
          onDelete={onDeleteFiles}
          errorText={errorMessage}
        />
      )}

      {!!errorMessage && <InlineFormError errorMessage={errorMessage} />}
    </div>
  );
};
